// Gemeinsame Teile der Shells. Behelf, bis `steuerung` IconButton, Tooltip und Sheet liefert
// (UI-Wunsch ui-basis); die Shells sollen danach auf die steuerung-Bauteile umgestellt werden.
import { type Icon as PhosphorIcon, X } from '@phosphor-icons/react';
import { Dialog, Tooltip } from 'radix-ui';
import {
  type ComponentPropsWithRef,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { cn } from '../kern/cn';
import { cva } from '../kern/cva';
import { RouterLink } from '../kern/Link';

/** Ungelesen-Zähler: ab 10 „9+“ (UX §2.5). */
export function formatCount(count: number): string {
  return count > 9 ? '9+' : String(count);
}

export function CountBadge({
  count,
  tone = 'notify',
  cap = true,
  className,
}: {
  count: number;
  tone?: 'notify' | 'unread' | 'neutral' | 'warning';
  /** „9+“ ab 10 (Ungelesen); Admin-Zähler zeigen die volle Zahl. */
  cap?: boolean;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden
      data-slot="count-badge"
      className={cn(
        'inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1 text-nav font-bold tabular-nums',
        tone === 'notify' && 'bg-notify text-on-notify',
        tone === 'unread' && 'bg-unread text-on-unread',
        tone === 'neutral' && 'bg-badge text-badge-fg',
        tone === 'warning' && 'bg-warning-soft text-warning-fg',
        className,
      )}
    >
      {cap ? formatCount(count) : count}
    </span>
  );
}

const iconButtonVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center rounded-full transition-[background-color,color,transform] duration-(--bar-dur-fast) ease-standard active:scale-97 disabled:cursor-not-allowed disabled:text-fg-disabled',
  {
    variants: {
      size: { md: 'size-10', sm: 'size-8' },
      tone: {
        nav: 'text-fg-nav hover:bg-pill-page hover:text-fg data-[state=open]:bg-pill-page data-[state=open]:text-fg',
        fg: 'text-fg hover:bg-pill-page data-[state=open]:bg-pill-page',
        card: 'text-fg-secondary hover:bg-pill-card hover:text-fg data-[state=open]:bg-pill-card',
      },
    },
    defaultVariants: { size: 'md', tone: 'nav' },
  },
);

export interface ShellIconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  icon: PhosphorIcon;
  /** Pflicht: Name und Tooltip (DESIGN §3.0). */
  label: string;
  /** Zahl oder Punkt oben rechts. */
  badge?: number | 'dot';
  /** Bei `href` ein Link statt Knopf. */
  href?: string;
  reload?: boolean;
  size?: 'md' | 'sm';
  tone?: 'nav' | 'fg' | 'card';
  /** Icon kräftiger, z. B. Glocke mit Ungelesenem (`fg` statt `fg-nav`). */
  emphasized?: boolean;
  weight?: 'regular' | 'fill';
  tooltip?: boolean;
}

/** Runder Icon-Knopf 40 (32 klein) mit Tooltip und optionalem Badge. */
export function ShellIconButton({
  icon: Glyph,
  label,
  badge,
  href,
  reload,
  size,
  tone,
  emphasized,
  weight = 'regular',
  tooltip = true,
  className,
  ref,
  ...rest
}: ShellIconButtonProps) {
  const classes = cn(iconButtonVariants({ size, tone }), emphasized && 'text-fg', className);
  const content = (
    <>
      <Glyph size={size === 'sm' ? 18 : 22} weight={weight} aria-hidden />
      {badge === 'dot' ? (
        <span
          aria-hidden
          className="absolute top-2 right-2 size-2 rounded-full bg-notify ring-2 ring-page"
        />
      ) : typeof badge === 'number' && badge > 0 ? (
        <CountBadge count={badge} className="absolute -top-0.5 -right-0.5 ring-2 ring-page" />
      ) : null}
    </>
  );
  const name = typeof badge === 'number' && badge > 0 ? `${label}, ${badge} unread` : label;
  const control = href ? (
    <RouterLink href={href} reload={reload} aria-label={name} className={classes}>
      {content}
    </RouterLink>
  ) : (
    <button ref={ref} type="button" aria-label={name} className={classes} {...rest}>
      {content}
    </button>
  );
  if (!tooltip) return control;
  const tip = (
    <Tooltip.Root delayDuration={400}>
      <Tooltip.Trigger asChild>{control}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="bottom"
          sideOffset={6}
          className="z-(--bar-z-tooltip) rounded-sm bg-overlay px-2 py-1 text-small text-fg shadow-2 data-[state=delayed-open]:animate-fade-in"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
  return <WithTooltipProvider>{tip}</WithTooltipProvider>;
}

const InTooltipProvider = createContext(false);

/** Tooltip-Provider für alle Shells (eine Instanz je Plattform). */
export function TooltipProvider({
  delayDuration = 400,
  children,
}: {
  delayDuration?: number;
  children: ReactNode;
}) {
  return (
    <InTooltipProvider.Provider value={true}>
      <Tooltip.Provider delayDuration={delayDuration}>{children}</Tooltip.Provider>
    </InTooltipProvider.Provider>
  );
}

/** Einzelne Shell-Teile (Galerie, Seiten außerhalb der Shell) bringen ihren Provider selbst mit. */
function WithTooltipProvider({ children }: { children: ReactNode }) {
  return useContext(InTooltipProvider) ? children : <TooltipProvider>{children}</TooltipProvider>;
}

const drawerVariants = cva('fixed z-(--bar-z-sheet) flex flex-col bg-card shadow-3 outline-none', {
  variants: {
    side: {
      left: 'inset-y-0 left-0 w-[min(300px,calc(100vw-48px))] rounded-r-xl data-[state=open]:animate-slide-in-left data-[state=closed]:animate-slide-out-left',
      right:
        'inset-y-0 right-0 w-[min(400px,100vw)] rounded-l-xl data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right',
      bottom:
        'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl pb-[env(safe-area-inset-bottom)] data-[state=open]:animate-slide-in-bottom data-[state=closed]:animate-slide-out-bottom',
    },
  },
  defaultVariants: { side: 'left' },
});

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: 'left' | 'right' | 'bottom';
  /** Zugänglicher Titel (sichtbar, wenn `showTitle`). */
  title: string;
  showTitle?: boolean;
  children: ReactNode;
  className?: string;
}

/** Schublade mit Scrim (Radix Dialog): mobile Navigation, „More“, Menüs als Sheet. */
export function Drawer({
  open,
  onOpenChange,
  side = 'left',
  title,
  showTitle,
  children,
  className,
}: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-(--bar-z-sheet) bg-scrim data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          aria-describedby={undefined}
          className={drawerVariants({ side, className })}
        >
          {side === 'bottom' ? (
            <span aria-hidden className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line" />
          ) : null}
          <div
            className={cn(
              'flex shrink-0 items-center justify-between gap-2 px-4',
              showTitle ? 'h-14' : 'sr-only',
            )}
          >
            <Dialog.Title className="truncate text-title-detail text-fg">{title}</Dialog.Title>
            {showTitle ? (
              <Dialog.Close asChild>
                <ShellIconButton icon={X} label="Close" tone="card" size="sm" tooltip={false} />
              </Dialog.Close>
            ) : null}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function subscribeMedia(query: string) {
  return (notify: () => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
      return () => undefined;
    const media = window.matchMedia(query);
    media.addEventListener('change', notify);
    return () => media.removeEventListener('change', notify);
  };
}

/** true, solange die Media-Query passt (ohne matchMedia, z. B. in Tests: `fallback`). */
export function useMediaQuery(query: string, fallback = true): boolean {
  const subscribe = useCallback((notify: () => void) => subscribeMedia(query)(notify), [query]);
  return useSyncExternalStore(
    subscribe,
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : fallback),
    () => fallback,
  );
}

/** true ab `md` (768): Rail statt Bottom-Nav, Dock statt Chat-Seite. */
export const useIsDesktop = () => useMediaQuery('(min-width: 768px)');

/** true, sobald die Seite mehr als `threshold` px gescrollt ist (Topbar-Linie ab 8 px). */
export function useScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [threshold]);
  return scrolled;
}

/** Pfeiltasten-Navigation (Roving) in einer Liste von Links/Knöpfen mit `[data-roving]`. */
export function moveRovingFocus(
  event: globalThis.KeyboardEvent,
  container: HTMLElement,
  vertical = true,
) {
  const keys = vertical ? ['ArrowDown', 'ArrowUp'] : ['ArrowRight', 'ArrowLeft'];
  if (![...keys, 'Home', 'End'].includes(event.key)) return;
  if (event.target instanceof HTMLInputElement) return;
  const items = [...container.querySelectorAll<HTMLElement>('[data-roving]')];
  if (items.length === 0) return;
  const index = items.indexOf(document.activeElement as HTMLElement);
  let next = index;
  if (event.key === keys[0]) next = index < 0 ? 0 : (index + 1) % items.length;
  if (event.key === keys[1]) next = index <= 0 ? items.length - 1 : index - 1;
  if (event.key === 'Home') next = 0;
  if (event.key === 'End') next = items.length - 1;
  event.preventDefault();
  items[next]?.focus();
}

/**
 * Roving Tabindex (DESIGN §3.1.2, §3.2.2): genau ein `[data-roving]`-Element ist Tab-Stopp – das
 * zuletzt fokussierte, sonst der aktive Eintrag (`aria-current="page"`), sonst der erste.
 */
export function syncRovingTabStops(container: HTMLElement, focused?: Element | null): void {
  const items = [...container.querySelectorAll<HTMLElement>('[data-roving]')];
  const current =
    (focused && items.find((item) => item === focused)) ??
    items.find((item) => item.getAttribute('aria-current') === 'page') ??
    items[0];
  for (const item of items) item.tabIndex = item === current ? 0 : -1;
}

/**
 * Ref für einen Navigations-Container: ein Tab-Stopp für die ganze Navigation, ↑/↓ (bzw. ←/→),
 * Home und End bewegen den Fokus zwischen `[data-roving]`-Elementen. Als nativer Listener, weil
 * `nav` kein interaktives Element ist; neue Einträge und Routenwechsel gleicht ein Observer ab.
 */
export function useRovingFocus<T extends HTMLElement>(vertical = true) {
  return useCallback(
    (node: T | null) => {
      if (!node) return;
      const onKey = (event: globalThis.KeyboardEvent) => moveRovingFocus(event, node, vertical);
      const onFocusIn = (event: FocusEvent) => syncRovingTabStops(node, event.target as Element);
      const onFocusOut = (event: FocusEvent) => {
        if (!node.contains(event.relatedTarget as Node | null)) syncRovingTabStops(node);
      };
      syncRovingTabStops(node);
      const observer =
        typeof MutationObserver === 'function'
          ? new MutationObserver(() => {
              if (!node.contains(document.activeElement)) syncRovingTabStops(node);
            })
          : undefined;
      observer?.observe(node, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });
      node.addEventListener('keydown', onKey);
      node.addEventListener('focusin', onFocusIn);
      node.addEventListener('focusout', onFocusOut);
      return () => {
        observer?.disconnect();
        node.removeEventListener('keydown', onKey);
        node.removeEventListener('focusin', onFocusIn);
        node.removeEventListener('focusout', onFocusOut);
      };
    },
    [vertical],
  );
}
